/* ---------------------------------------------------------------------------
 * Quick-add card — the link back to the product page
 *
 * Horizon renders a "view more details" block on the PDP but not inside this
 * card. Checked on the live theme: the modal's DOM holds no `.view-more-details`
 * element at all — only a stylesheet rule in `quick-add-modal-styles.liquid`
 * that would hide one — so the card had no route to the product page.
 *
 * The link is built from the title's own `href`, which already carries the
 * variant, so it cannot drift from the product on show.
 *
 * This sits in its own asset rather than in `ryoxzel-pdp.js` because the card
 * opens from a product card on any template — the homepage and collections
 * included — while `ryoxzel-pdp.js` is gated on the RYOXZEL product templates by
 * `ryoxzel_pdp_suffixes` in `snippets/scripts.liquid`. Measured on the homepage:
 * no `ryoxzel-pdp.js` script tag is present at all.
 *
 * The label is a UI string rather than merchant copy, which is why it is here
 * and not a section setting.
 * ------------------------------------------------------------------------- */

import { enhanceGalleryLoop } from '@ryoxzel/gallery-loop';
import { initQuantityTiers } from '@ryoxzel/quantity-tiers';

const QUICK_ADD_DETAILS_CLASS = 'ryoxzel-quick-add-details';
const QUICK_ADD_DETAILS_LABEL = 'View full details';

/* The card's media runs the same gallery loop as the PDP's mobile gallery — the
 * merchant asked for exactly that, and `@ryoxzel/gallery-loop` is the one
 * implementation of it, shared with `assets/ryoxzel-pdp.js`.
 *
 * Horizon's own `slideshow-arrows` are hidden in `ryoxzel-variant.css`: the
 * RYOXZEL controls are this site's carousel control, and `enhanceGalleryLoop`
 * unhides them (`controls.hidden = false`) the way it does on the mobile gallery.
 * That is what replaces Horizon's near-black default arrows, whose hover mixed to
 * a dark plum rather than the rail control's lavender.
 */
function initQuickAddGalleryLoop(dialog) {
  const gallery = dialog.querySelector('media-gallery');
  if (!gallery) return;

  /* Once only. The loop watches the slides itself and rebuilds its boundary
     clones when the media list changes, so a second call has nothing to add —
     and calling `_ryxGalleryRefresh()` on every mutation of the card actively
     fought the reader. `refresh()` re-aligns the rail to Horizon's active slide,
     and this runs on every DOM change in the dialog, so every scroll was pulled
     back within the same frame: measured, a manual scroll to 357 read 714 on the
     next animation frame, and `_ryxGalleryRefresh()` moved 357 to 714 on demand.
     Horizon mutating anything — the dots, the slides — was enough to undo a step. */
  if (gallery.dataset.ryxGalleryReady === 'true') return;

  /* Desktop only. The phone sheet's media is a 75px thumbnail beside the title —
     a carousel there would put a 44px control over most of the frame — so the
     loop, and with it the RYOXZEL controls, stays off below 750px. */
  enhanceGalleryLoop(gallery, { mediaQuery: '(min-width: 750px)' });
}


function initQuickAddDetails(dialog) {
  const content = dialog.querySelector('.quick-add-modal__content');
  const details = content?.querySelector('.product-details') || content;
  if (!details) return;

  /* Horizon uses two structures for this card. Desktop: `.product-details`, with
     the title in `.view-product-title`. Mobile: no `.product-details` at all —
     measured at 390px, the content holds `.product-information__media`,
     `.product-header`, `variant-picker` and `product-form-component` — so the
     title link lives in `.product-header`. Both are read. */
  const titleLink = details.querySelector('.view-product-title a[href], .product-header a[href]');

  /* The host has to be somewhere Horizon does not hide. On desktop it hides every
     descendant of `.product-details` that is not on an allow-list, with
     `display: none !important`:
       `.quick-add-modal__content .product-details *:not(.group-block,
        .group-block-content, .buy-buttons-block, .buy-buttons-block *, ...)`
     A link appended to `.product-details` itself is outside that list and measured
     `display: none` — present, correct, invisible. `.buy-buttons-block *` is on
     the list; on mobile there is no such wrapper, so the form component takes it. */
  const host =
    details.querySelector('.buy-buttons-block') ||
    details.querySelector('product-form-component') ||
    details;

  const existing = details.querySelector(`.${QUICK_ADD_DETAILS_CLASS}`);

  // One dialog serves every product, so a link left over from the last one
  // would point at the wrong page.
  if (!titleLink) {
    existing?.remove();
    return;
  }

  const href = titleLink.getAttribute('href');
  if (existing) {
    if (existing.parentElement !== host) host.append(existing);
    if (existing.getAttribute('href') !== href) existing.setAttribute('href', href);
    return;
  }

  const link = document.createElement('a');
  link.className = QUICK_ADD_DETAILS_CLASS;
  link.setAttribute('href', href);
  link.textContent = QUICK_ADD_DETAILS_LABEL;
  host.append(link);
}

/* Horizon injects the card's content after the dialog opens, so this watches the
   dialog rather than running once. Adding the link is itself a mutation, and the
   next pass finds it already in place and stops.

   The tier cards come through here too. Their own inline bootstrap does not run
   in this dialog — Horizon builds the content with `innerHTML`, and an inline
   script inserted that way never executes — so the block arrives hidden and inert
   and this is what marks it ready. */
function watchQuickAddModals(root = document) {
  for (const dialog of root.querySelectorAll('dialog.quick-add-modal')) {
    if (dialog.dataset.ryxDetailsWatched === 'true') continue;
    dialog.dataset.ryxDetailsWatched = 'true';

    const observer = new MutationObserver(() => {
      if (dialog._ryxDetailsFrame) return;
      dialog._ryxDetailsFrame = window.requestAnimationFrame(() => {
        dialog._ryxDetailsFrame = 0;
        initQuickAddGalleryLoop(dialog);
        initQuickAddDetails(dialog);
        initQuantityTiers(dialog);
      });
    });

    observer.observe(dialog, { childList: true, subtree: true });
    dialog._ryxDetailsObserver = observer;
  }
}

watchQuickAddModals();

document.addEventListener('shopify:section:load', () => {
  watchQuickAddModals();
});
